import { HTMLAttributes } from "react";
import { cn } from "@/utils/cn";
import { Button } from "@/components/common/Button/Button";

interface SecondaryHeroProps extends HTMLAttributes<HTMLDivElement> {
  tag?: {
    text: string;
    href: string;
  };
  title?: string;
  subTitle?: string;
  description: string;
  cta?: {
    primary: {
      text: string;
      href: string;
    };
    secondary?: {
      text: string;
      href: string;
    };
  };
  isExternalLink?: boolean;
}

const SecondaryHero = ({
  tag,
  title,
  subTitle,
  description,
  cta,
  isExternalLink,
  className,
  ...props
}: SecondaryHeroProps) => {
  return (
    <section
      className={cn(
        "relative overflow-hidden bg-background pt-16 pb-12 md:pt-32 md:pb-16",
        className
      )}
      {...props}
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-light opacity-50" />

      <div className="container relative mx-auto px-4">
        {/* Content */}
        <div className="mx-auto max-w-[800px] text-center">
          {/* Tag */}
          {tag && (
            <a
              href={tag.href}
              className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 md:px-4 md:py-1.5 text-xs md:text-sm font-medium text-primary-600 transition-colors hover:bg-primary-100 mb-4 md:mb-6"
            >
              <span className="relative">{tag.text}</span>
              <svg
                className="ml-1 md:ml-2 h-3 w-3 md:h-4 md:w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </a>
          )}

          {/* Integrations Heading */}
          {title &&  <h4 className="text-sm md:text-base font-semibold text-primary-600 tracking-wider mb-3 md:mb-4">
            {title}
          </h4>}


          {/* Title */}
          <h2 className="mb-3 md:mb-4 text-2xl md:text-4xl font-bold text-gray-900">
            {subTitle}
          </h2>

          {/* Description */}
          <p className="mb-6 md:mb-8 text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
            {description}
          </p>

          {/* CTAs */}
          {cta && (
            <div className="flex flex-row items-center justify-center gap-3 md:gap-4 mb-10 md:mb-16">
              <Button
                variant="solid"
                color="primary"
                size="default"
                className="text-sm md:text-base py-2.5 px-4 md:px-5"
                onClick={() => {
                  if (isExternalLink) {
                    window.open(cta.primary.href, '_blank');
                  } else {
                    window.location.href = cta.primary.href;
                  }
                }}
              >
                {cta.primary.text}
              </Button>
              {cta.secondary && (
                <Button
                  variant="gradient"
                  color="primary"
                  size="default"
                  className="text-sm md:text-base py-2.5 px-4 md:px-5"
                  onClick={() => {
                    if (isExternalLink) {
                      window.open(cta.secondary!.href, '_blank');
                    } else {
                      window.location.href = cta.secondary!.href;
                    }
                  }}
                >
                  {cta.secondary!.text}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SecondaryHero;